import { useState, useEffect, useCallback } from 'react';

interface SectionItem {
  id: string;
  label: string;
  icon: string;
}

const sections: SectionItem[] = [
  { id: 'hero', label: 'Início', icon: 'ri-home-heart-line' },
  { id: 'timeline', label: 'História', icon: 'ri-book-open-line' },
  { id: 'proposito', label: 'Propósito', icon: 'ri-compass-3-line' },
  { id: 'conquistas', label: 'Conquistas', icon: 'ri-trophy-line' },
  { id: 'missao', label: 'Missão', icon: 'ri-heart-line' },
  { id: 'jornada', label: 'Jornada', icon: 'ri-footprint-line' },
  { id: 'midia', label: 'Mídia', icon: 'ri-newspaper-line' },
  { id: 'depoimentos', label: 'Depoimentos', icon: 'ri-chat-heart-line' },
  { id: 'videos', label: 'Vídeos', icon: 'ri-video-line' },
  { id: 'valores', label: 'Valores', icon: 'ri-heart-pulse-line' },
  { id: 'para-quem', label: 'Para Quem', icon: 'ri-user-heart-line' },
  { id: 'estatisticas', label: 'Impacto', icon: 'ri-bar-chart-grouped-line' },
  { id: 'metodo', label: 'Método', icon: 'ri-route-line' },
  { id: 'faq', label: 'FAQ', icon: 'ri-question-line' },
  { id: 'instagram', label: 'Instagram', icon: 'ri-instagram-line' },
  { id: 'cta-final', label: 'Comece Agora', icon: 'ri-quill-pen-line' },
];

const SectionNav = () => {
  const [activeSection, setActiveSection] = useState('hero');
  const [isVisible, setIsVisible] = useState(false);
  const [hoveredItem, setHoveredItem] = useState<string | null>(null);

  const handleScroll = useCallback(() => {
    const scrollY = window.scrollY;
    setIsVisible(scrollY > 300);

    const offset = 200;
    for (let i = sections.length - 1; i >= 0; i--) {
      const el = document.getElementById(sections[i].id);
      if (el) {
        const top = el.getBoundingClientRect().top + scrollY - offset;
        if (scrollY >= top) {
          setActiveSection(sections[i].id);
          break;
        }
      }
    }
  }, []);

  useEffect(() => {
    let ticking = false;
    const onScroll = () => {
      if (!ticking) {
        window.requestAnimationFrame(() => {
          handleScroll();
          ticking = false;
        });
        ticking = true;
      }
    };
    window.addEventListener('scroll', onScroll, { passive: true });
    handleScroll();
    return () => window.removeEventListener('scroll', onScroll);
  }, [handleScroll]);

  const scrollToSection = (id: string) => {
    const el = document.getElementById(id);
    if (el) {
      el.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  };

  return (
    <div
      className="fixed right-5 top-1/2 -translate-y-1/2 z-40 transition-all duration-700 ease-out"
      style={{
        opacity: isVisible ? 1 : 0,
        transform: isVisible
          ? 'translateY(-50%) translateX(0)'
          : 'translateY(-50%) translateX(20px)',
        pointerEvents: isVisible ? 'auto' : 'none',
      }}
    >
      <div className="relative flex flex-col items-end gap-1">
        {/* Linha vertical de progresso */}
        <div className="absolute right-[13px] top-2 bottom-2 w-px bg-gold-500/10 rounded-full" />
        <div
          className="absolute right-[13px] top-2 w-px bg-gradient-to-b from-gold-500/60 to-gold-400/40 rounded-full transition-all duration-500 ease-out"
          style={{
            height: `${
              ((sections.findIndex((s) => s.id === activeSection) + 1) /
                sections.length) *
              100
            }%`,
            maxHeight: 'calc(100% - 16px)',
          }}
        />

        {sections.map((section) => {
          const isActive = activeSection === section.id;
          const isHovered = hoveredItem === section.id;

          return (
            <div
              key={section.id}
              className="relative flex items-center gap-2"
              onMouseEnter={() => setHoveredItem(section.id)}
              onMouseLeave={() => setHoveredItem(null)}
            >
              {/* Label tooltip */}
              <div
                className="absolute right-9 whitespace-nowrap transition-all duration-300 ease-out pointer-events-none"
                style={{
                  opacity: isHovered || isActive ? 1 : 0,
                  transform:
                    isHovered || isActive
                      ? 'translateX(0) scale(1)'
                      : 'translateX(8px) scale(0.9)',
                }}
              >
                <span
                  className={`font-['Montserrat'] text-[10px] tracking-wider uppercase px-3 py-1.5 rounded-md backdrop-blur-md border transition-all duration-300 ${
                    isActive
                      ? 'bg-gold-500/20 text-gold-400 border-gold-500/30 shadow-[0_0_12px_rgba(201,168,76,0.15)]'
                      : 'bg-luxury-charcoal/80 text-white/60 border-white/10'
                  }`}
                >
                  {section.label}
                </span>
              </div>

              {/* Dot / indicator */}
              <button
                onClick={() => scrollToSection(section.id)}
                className={`relative z-10 flex items-center justify-center cursor-pointer transition-all duration-400 ease-out ${
                  isActive
                    ? 'w-7 h-7'
                    : isHovered
                    ? 'w-6 h-6'
                    : 'w-[26px] h-[26px]'
                }`}
                aria-label={`Ir para ${section.label}`}
              >
                {isActive ? (
                  <div className="relative">
                    <div className="w-7 h-7 rounded-full bg-gradient-to-br from-gold-500 to-gold-600 flex items-center justify-center shadow-[0_0_16px_rgba(201,168,76,0.35)]">
                      <i
                        className={`${section.icon} text-[10px] text-luxury-black`}
                      />
                    </div>
                    <div className="absolute inset-0 rounded-full bg-gold-500/30 animate-ping" style={{ animationDuration: '2s' }} />
                  </div>
                ) : (
                  <div
                    className={`rounded-full transition-all duration-300 flex items-center justify-center ${
                      isHovered
                        ? 'w-5 h-5 bg-gold-500/30 border border-gold-500/50'
                        : 'w-2.5 h-2.5 bg-white/20 border border-white/10 hover:bg-gold-500/20'
                    }`}
                  >
                    {isHovered && (
                      <i
                        className={`${section.icon} text-[9px] text-gold-400/80`}
                      />
                    )}
                  </div>
                )}
              </button>
            </div>
          );
        })}
      </div>
    </div>
  );
};

export default SectionNav;